import * as THREE from 'three';

// Helper: Generate points uniformly within/on a box
export const getBoxPoints = (
  width: number, 
  height: number, 
  depth: number, 
  count: number, 
  center = new THREE.Vector3(0, 0, 0)
) => {
  const points = [];
  const halfW = width / 2;
  const halfH = height / 2;
  const halfD = depth / 2;

  // Surface area calculation for distribution
  const areaFrontBack = width * height;
  const areaLeftRight = depth * height;
  const areaTopBottom = width * depth;
  const totalArea = 2 * (areaFrontBack + areaLeftRight + areaTopBottom);

  for (let i = 0; i < count; i++) {
    const r = Math.random() * totalArea;
    let x, y, z;

    if (r < 2 * areaFrontBack) {
      // Front or Back
      x = (Math.random() - 0.5) * width;
      y = (Math.random() - 0.5) * height;
      z = (r < areaFrontBack) ? halfD : -halfD;
    } else if (r < 2 * (areaFrontBack + areaLeftRight)) {
      // Left or Right
      z = (Math.random() - 0.5) * depth;
      y = (Math.random() - 0.5) * height;
      x = (r < 2 * areaFrontBack + areaLeftRight) ? halfW : -halfW;
    } else {
      // Top or Bottom
      x = (Math.random() - 0.5) * width;
      z = (Math.random() - 0.5) * depth;
      y = (r < 2 * (areaFrontBack + areaLeftRight) + areaTopBottom) ? halfH : -halfH;
    }

    points.push(x + center.x, y + center.y, z + center.z);
  }
  return points;
};

// Helper: Generate points in a sphere (volume or surface)
export const getSpherePoints = (
  radius: number, 
  count: number, 
  center = new THREE.Vector3(0, 0, 0),
  surfaceOnly = false
) => {
  const points = [];
  for (let i = 0; i < count; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    // If surfaceOnly, r is fixed. If volume, r is random (cubic root for uniform distribution)
    const r = surfaceOnly ? radius : radius * Math.cbrt(Math.random());
    
    const x = r * Math.sin(phi) * Math.cos(theta);
    const y = r * Math.sin(phi) * Math.sin(theta);
    const z = r * Math.cos(phi);
    
    points.push(x + center.x, y + center.y, z + center.z);
  }
  return points;
};

// Helper: Generate points on a cylinder (approximate)
export const getCylinderPoints = (
    radius: number,
    height: number,
    count: number,
    center = new THREE.Vector3(0, 0, 0)
) => {
    const points = [];
    const areaSide = 2 * Math.PI * radius * height;
    const areaTopBottom = 2 * Math.PI * radius * radius;
    const totalArea = areaSide + 2 * areaTopBottom;

    for (let i = 0; i < count; i++) {
        const r = Math.random() * totalArea;
        let x, y, z;

        if (r < areaSide) {
            // Side
            const theta = Math.random() * Math.PI * 2;
            x = radius * Math.cos(theta);
            z = radius * Math.sin(theta);
            y = (Math.random() - 0.5) * height;
        } else {
            // Top or Bottom
            const theta = Math.random() * Math.PI * 2;
            const rDisk = radius * Math.sqrt(Math.random());
            x = rDisk * Math.cos(theta);
            z = rDisk * Math.sin(theta);
            y = (r < areaSide + areaTopBottom) ? height / 2 : -height / 2;
        }
        points.push(x + center.x, y + center.y, z + center.z);
    }
    return points;
}


// --- Specific Shape Generators ---

const generateMobileShape = (count: number) => {
    // Phone Body - Modern sleek design (just rounded corners)
    const points: number[] = [];

    // Main Body - slightly thinner and taller for modern look
    // We simulate rounded corners by using a slightly smaller box and adding cylinders at corners? 
    // Or just a simple clean box for the particle effect which is abstract anyway.
    // Let's make it a clean block.
    points.push(...getBoxPoints(1.6, 3.2, 0.15, count));

    return new Float32Array(points);
};

const generateDesktopShape = (count: number) => {
    const screenCount = Math.floor(count * 0.6);
    const standCount = Math.floor(count * 0.1);
    const baseCount = Math.floor(count * 0.1);
    const keyboardCount = Math.floor(count * 0.15);
    const mouseCount = count - screenCount - standCount - baseCount - keyboardCount;

    const points: number[] = [];

    // Monitor Screen
    points.push(...getBoxPoints(3.8, 2.2, 0.1, screenCount, new THREE.Vector3(0, 0.5, 0)));

    // Stand
    points.push(...getCylinderPoints(0.1, 1.0, standCount, new THREE.Vector3(0, -0.5, -0.1)));

    // Base
    points.push(...getBoxPoints(1.2, 0.1, 0.8, baseCount, new THREE.Vector3(0, -1.0, -0.1)));

    // Keyboard (Angled slightly)
    // We'll just place it flat in front
    points.push(...getBoxPoints(2.5, 0.1, 0.8, keyboardCount, new THREE.Vector3(0, -1.0, 1.0)));

    // Mouse
    points.push(...getSpherePoints(0.15, mouseCount, new THREE.Vector3(1.8, -1.0, 1.0)));

    return new Float32Array(points);
};

const generateGlobeShape = (count: number) => {
    const points: number[] = [];
    const radius = 1.8;
    
    // Generate points along latitude lines (rings)
    const latCount = 12;
    const pointsPerLat = Math.floor((count * 0.6) / latCount);
    
    for(let i = 0; i < latCount; i++) {
        // Map i to -PI/2 to PI/2
        const phi = -Math.PI/2 + (Math.PI * (i + 1) / (latCount + 1));
        const ringRadius = radius * Math.cos(phi);
        const y = radius * Math.sin(phi);
        
        for(let j = 0; j < pointsPerLat; j++) {
            const theta = (j / pointsPerLat) * Math.PI * 2;
            const x = ringRadius * Math.cos(theta);
            const z = ringRadius * Math.sin(theta);
            points.push(x, y, z);
        }
    }

    // Generate points along longitude lines
    const longCount = 12;
    const pointsPerLong = Math.floor((count * 0.4) / longCount);
    
    for(let i = 0; i < longCount; i++) {
        const theta = (i / longCount) * Math.PI * 2;
        
        for(let j = 0; j < pointsPerLong; j++) {
            const phi = -Math.PI/2 + (Math.PI * j / pointsPerLong);
            const x = radius * Math.cos(phi) * Math.cos(theta);
            const y = radius * Math.sin(phi);
            const z = radius * Math.cos(phi) * Math.sin(theta);
            points.push(x, y, z);
        }
    }

    return new Float32Array(points);
};

const generateBrowserShape = (count: number) => {
    // Replaced with Globe Shape as requested for "Website"
    return generateGlobeShape(count);
};

const generateDashboardShape = (count: number) => {
    const points: number[] = [];
    
    // Sidebar (Left vertical strip)
    const sidebarCount = Math.floor(count * 0.15);
    points.push(...getBoxPoints(0.6, 2.8, 0.1, sidebarCount, new THREE.Vector3(-1.6, 0, 0)));

    // Header (Top horizontal strip)
    const headerCount = Math.floor(count * 0.1);
    points.push(...getBoxPoints(3.0, 0.5, 0.1, headerCount, new THREE.Vector3(0.2, 1.15, 0)));

    // Main Chart (Large rectangle)
    const chartCount = Math.floor(count * 0.4);
    points.push(...getBoxPoints(2.0, 1.2, 0.1, chartCount, new THREE.Vector3(-0.3, 0.2, 0)));

    // Side Panel / Stats (Right side vertical)
    const statsCount = Math.floor(count * 0.2);
    points.push(...getBoxPoints(0.8, 1.2, 0.1, statsCount, new THREE.Vector3(1.3, 0.2, 0)));

    // Bottom Row (3 small cards)
    const bottomCount = count - sidebarCount - headerCount - chartCount - statsCount;
    const cardCount = Math.floor(bottomCount / 3);
    
    points.push(...getBoxPoints(0.9, 0.8, 0.1, cardCount, new THREE.Vector3(-0.85, -0.9, 0)));
    points.push(...getBoxPoints(0.9, 0.8, 0.1, cardCount, new THREE.Vector3(0.2, -0.9, 0)));
    points.push(...getBoxPoints(0.9, 0.8, 0.1, cardCount, new THREE.Vector3(1.25, -0.9, 0)));

    return new Float32Array(points);
};

const generateCloudShape = (count: number) => {
    // A cloud made of overlapping spheres
    const points: number[] = [];
    
    // Central large sphere
    const centerCount = Math.floor(count * 0.4);
    points.push(...getSpherePoints(1.2, centerCount, new THREE.Vector3(0, 0, 0)));

    // Left sphere
    const leftCount = Math.floor(count * 0.2);
    points.push(...getSpherePoints(0.9, leftCount, new THREE.Vector3(-1.2, -0.2, 0)));

    // Right sphere
    const rightCount = Math.floor(count * 0.2);
    points.push(...getSpherePoints(0.9, rightCount, new THREE.Vector3(1.2, -0.1, 0)));

    // Top sphere
    const topCount = Math.floor(count * 0.1);
    points.push(...getSpherePoints(0.7, topCount, new THREE.Vector3(0.5, 0.8, 0.2)));

    // Bottom/Front filler
    const fillCount = count - centerCount - leftCount - rightCount - topCount;
    points.push(...getSpherePoints(0.6, fillCount, new THREE.Vector3(-0.4, -0.5, 0.3)));

    return new Float32Array(points);
};

const generateRackShape = (count: number) => {
    // Server Rack
    const frameCount = Math.floor(count * 0.3);
    const serverCount = count - frameCount;
    
    const points: number[] = [];

    // Frame
    points.push(...getBoxPoints(2.2, 3.5, 1.5, frameCount, new THREE.Vector3(0, 0, 0)));

    // Servers (Stacked units)
    const units = 6;
    const pointsPerUnit = Math.floor(serverCount / units);
    
    for(let i = 0; i < units; i++) {
        const y = -1.2 + (i * 0.5);
        // Server unit
        points.push(...getBoxPoints(1.8, 0.3, 1.2, pointsPerUnit, new THREE.Vector3(0, y, 0.1)));
        
        // Blinking lights (simulated by just placing points at the front)
        // We can't easily animate color here without complex shader attributes, 
        // but we can place dense points to look like LEDs
    }

    return new Float32Array(points);
};

const generateNetworkShape = (count: number) => {
    // Network / Globe
    const points: number[] = [];
    
    // Central Globe
    const globeCount = Math.floor(count * 0.7);
    points.push(...getSpherePoints(1.8, globeCount, new THREE.Vector3(0, 0, 0), true));

    // Orbiting Satellites / Nodes
    const satelliteCount = count - globeCount;
    const numSatellites = 6;
    const pointsPerSat = Math.floor(satelliteCount / numSatellites);

    for(let i = 0; i < numSatellites; i++) {
        const angle = (i / numSatellites) * Math.PI * 2;
        const radius = 2.8;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        const y = (Math.random() - 0.5) * 2; // Random height variation
        
        points.push(...getSpherePoints(0.3, pointsPerSat, new THREE.Vector3(x, y, z)));
        
        // Connections (Lines) - Hard to do with points, but we can trail points between center and satellite?
        // Let's just leave them as floating nodes for now.
    }

    return new Float32Array(points);
};


export const generateServiceShape = (type: string, count: number): Float32Array => {
  switch (type) {
    case 'mobile-app':
      return generateMobileShape(count);
      
    case 'desktop-app':
      return generateDesktopShape(count);

    case 'website':
      return generateBrowserShape(count);

    case 'web-app':
      return generateDashboardShape(count);

    case 'server-software':
      return generateCloudShape(count);
      
    case 'server-admin':
      return generateRackShape(count);

    case 'it-consulting':
    default:
      return generateNetworkShape(count);
  }
};
